import { useEffect } from 'preact/hooks'
import { onScreen } from '../audio'
import { Hub } from './hub/Hub'
import { MatchmakingLayer } from './matchmaking/MatchmakingLayer'
import { PodiumScreen } from './podium/PodiumScreen'
import { LazyProfileCard } from './profile/LazyProfileCard'
import { RaceOverlay } from './RaceOverlay'
import { ReloadBanner } from './reload/ReloadButton'
import { ScreenBoundary } from './reload/ScreenBoundary'
import type { Session } from './session'
import { SettingsScreen } from './settings/SettingsScreen'
import { LazyShop } from './shop/LazyShop'
import { useStore } from './useStore'

/**
 * The screen for the session's flow (`menu → lobby → race → results`, PHASE1 T1.8): the hub
 * (T3.1), matchmaking (T3.2), the race, the podium results (T3.5), the profile card (T3.7, which
 * mounts T2.4's pieces; `ProfileScreen` is unrouted), the settings (T3.7) and the shop (T3.9a,
 * loaded on first open). The account's notice and switch dialog and the name dialog (T2.6) sit
 * over the hub and the profile card; the party bar (T2.7) over the hub and the results. A screen
 * that fails to render shows a Reload panel instead of a blank page, and a chunk that will not
 * load shows the Reload banner (D-066).
 */
export function App({ session }: { session: Session }) {
  const { screen } = useStore(session.flow)
  return (
    <>
      <ScreenBoundary resetKey={screen}>
        <Screen session={session} />
      </ScreenBoundary>
      <ReloadBanner />
    </>
  )
}

function Screen({ session }: { session: Session }) {
  const flow = useStore(session.flow)
  const room = useStore(session.room)
  const net = useStore(session.net.state)
  const auth = useStore(session.auth)
  const emotes = useStore(session.emotes)
  const { AuthNotice, NameDialog } = session
  const PartyBar = session.party.Bar
  // Hub music, and the results sting (PHASE3 T3.6).
  useEffect(() => onScreen(flow.screen), [flow.screen])

  switch (flow.screen) {
    case 'menu':
      return (
        <>
          <Hub session={session} flow={flow} auth={auth} net={net} />
          <NameDialog />
          <AuthNotice />
          <PartyBar />
        </>
      )
    case 'lobby':
    case 'race':
      // Matchmaking stays at one place in the tree, so its pedestals live on into the race
      // screen's hand-off (PHASE3 T3.2); the race's Leave bar sits above it.
      return (
        <>
          <MatchmakingLayer session={session} flow={flow} room={room} net={net} emotes={emotes} />
          {flow.screen === 'race' && <RaceOverlay onLeave={() => session.toMenu()} />}
        </>
      )
    case 'results':
      return (
        <>
          <PodiumScreen session={session} flow={flow} room={room} />
          <PartyBar place="bottom" />
        </>
      )
    case 'profile':
      return (
        <>
          <LazyProfileCard session={session} />
          <NameDialog />
          <AuthNotice />
        </>
      )
    case 'settings':
      return <SettingsScreen session={session} />
    case 'shop':
      return <LazyShop session={session} />
  }
}
